/**
 * Product Quiz Section JavaScript
 * Handles multi-step quiz navigation, selection logic, and product filtering
 */

(function () {
  "use strict";

  class ProductQuiz {
    constructor(sectionElement) {
      this.section = sectionElement;
      this.sectionId = this.section.dataset.sectionId;

      // Load configuration
      this.config = this.loadConfig();
      this.products = this.loadProducts();
      this.colorVariants = this.loadColorVariants();

      // State
      this.currentStep = 1;
      this.selections = {}; // { stepNumber: [{ blockId, filterType, filterValue }] }
      this.matchingProducts = []; // Deduplicated by base collection (shown as result cards)
      this.allMatchingProducts = []; // All products matching the quiz (used for color variant filtering)
      this.selectedVariants = {}; // { productId: variant }
      this.selectedColorProducts = {}; // { productId: colorVariantProductId } - tracks which color variant is selected

      // Initialize selections object for each step
      for (let i = 1; i <= this.config.stepCount; i++) {
        this.selections[i] = [];
      }

      // DOM Elements
      this.progressBar = this.section.querySelector("[data-quiz-progress]");
      this.progressWrapper = this.section.querySelector(".progress-wrapper");
      this.loadingOverlay = this.section.querySelector("[data-quiz-loading]");
      this.resultsContainer = this.section.querySelector(
        "[data-quiz-results-container]",
      );
      this.emptyResults = this.section.querySelector(
        "[data-quiz-empty-results]",
      );

      // Bind events
      this.bindEvents();

      // Update initial state
      this.updateProgressBar();
    }

    loadConfig() {
      const configElement = this.section.querySelector("[data-quiz-config]");
      if (configElement) {
        try {
          return JSON.parse(configElement.textContent);
        } catch (e) {
          console.error("Failed to parse quiz config:", e);
        }
      }
      return {
        stepCount: 4,
        resultsCount: 3,
        onlyAvailable: true,
        enableLoadingAnimation: true,
        buyButtonAction: "product_page",
        mainBadgeMode: "first",
        secondaryBadgeMode: "disabled",
        badgeText: "Mejor valor",
        secondaryLabelText: "También recomendado",
        btnBuyText: "Comprar ahora",
        steps: {},
      };
    }

    loadProducts() {
      const productsElement = this.section.querySelector(
        "[data-quiz-products]",
      );
      if (productsElement) {
        try {
          const products = JSON.parse(productsElement.textContent);
          return products;
        } catch (e) {
          console.error("[Quiz] Failed to parse products:", e);
        }
      }
      return [];
    }

    loadColorVariants() {
      const colorVariantsElement = this.section.querySelector(
        "[data-quiz-color-variants]",
      );
      if (colorVariantsElement) {
        try {
          const variants = JSON.parse(colorVariantsElement.textContent);
          return variants;
        } catch (e) {
          console.error("[Quiz] Failed to parse color variants:", e);
        }
      }
      return {};
    }

    getColorVariantsForProduct(product) {
      // Get the base collection handle from the product's metafields
      const baseCollection =
        product.metafields?.["apple.apl_BaseProductCollection"];

      if (!baseCollection || !this.colorVariants[baseCollection]) {
        return [];
      }

      // Get all color variants from the base collection
      // We show ALL color variants (not just those matching quiz filters)
      // because color is a visual preference, not a functional filter
      const allColorVariants = this.colorVariants[baseCollection];

      // Filter to only variants with matching option values (same config, different color)
      // Option1Value = color (excluded from comparison)
      // Option2-6 = other specs (must match)
      const matchingColorVariants = allColorVariants.filter((variant) => {
        return this.hasSameOptionValues(product, variant);
      });

      // Collapse same-color duplicates (case-insensitive on colorName); keep first occurrence
      const seen = new Set();
      const dedupedColorVariants = matchingColorVariants.filter((variant) => {
        const key = String(variant.colorName || variant.title || variant.id)
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // If no matching variants, just return the product itself as the only option
      return dedupedColorVariants.length > 0
        ? dedupedColorVariants
        : [product];
    }

    /**
     * Get all option values for a product (Option2Value through Option6Value)
     * Option1Value is excluded as it's the color
     */
    getProductOptionValues(product) {
      const metafields = product.metafields || {};
      return {
        option2: metafields["apple.apl_Option2Value"] || "",
        option3: metafields["apple.apl_Option3Value"] || "",
        option4: metafields["apple.apl_Option4Value"] || "",
        option5: metafields["apple.apl_Option5Value"] || "",
        option6: metafields["apple.apl_Option6Value"] || "",
      };
    }

    /**
     * Check if two products have the same option values (excluding color/Option1Value)
     * This determines if they are truly color variants of the same configuration
     */
    hasSameOptionValues(productA, productB) {
      const optionsA = this.getProductOptionValues(productA);
      const optionsB = this.getProductOptionValues(productB);

      const normalize = (val) =>
        String(val || "")
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "");

      const match2 =
        normalize(optionsA.option2) === normalize(optionsB.option2);
      const match3 =
        normalize(optionsA.option3) === normalize(optionsB.option3);
      const match4 =
        normalize(optionsA.option4) === normalize(optionsB.option4);
      const match5 =
        normalize(optionsA.option5) === normalize(optionsB.option5);
      const match6 =
        normalize(optionsA.option6) === normalize(optionsB.option6);

      return match2 && match3 && match4 && match5 && match6;
    }

    bindEvents() {
      // Option card clicks
      this.section.querySelectorAll(".option-card").forEach((card) => {
        card.addEventListener("click", (e) => this.handleOptionClick(e, card));
      });

      // Navigation buttons
      this.section.querySelectorAll("[data-quiz-next]").forEach((btn) => {
        btn.addEventListener("click", () => this.nextStep());
      });

      this.section.querySelectorAll("[data-quiz-back]").forEach((btn) => {
        btn.addEventListener("click", () => this.previousStep());
      });

      this.section.querySelectorAll("[data-quiz-results]").forEach((btn) => {
        btn.addEventListener("click", () => this.showResults());
      });

      this.section.querySelectorAll("[data-quiz-restart]").forEach((btn) => {
        btn.addEventListener("click", () => this.restart());
      });
    }

    handleOptionClick(event, card) {
      const step = parseInt(card.dataset.step);
      const blockId = card.dataset.optionBlock;
      const optionId = card.dataset.optionId || ""; // User-defined handle for dependencies
      const filterType = card.dataset.filterType;
      const filterValue = card.dataset.filterValue;
      const stepConfig = this.config.steps[step] || {};
      const maxSelections = stepConfig.maxSelections || "unlimited";

      // Check if already selected
      const isSelected = card.classList.contains("selected");
      const currentSelections = this.selections[step];

      if (isSelected) {
        // Deselect
        card.classList.remove("selected");
        this.selections[step] = currentSelections.filter(
          (s) => s.blockId !== blockId,
        );
      } else {
        // Check max selections
        if (maxSelections !== "unlimited") {
          const max = parseInt(maxSelections);

          if (currentSelections.length >= max) {
            // If max is 1, replace selection. Otherwise, don't allow more.
            if (max === 1) {
              // Deselect all others in this step
              this.section
                .querySelectorAll(`.option-card[data-step="${step}"].selected`)
                .forEach((c) => {
                  c.classList.remove("selected");
                });
              this.selections[step] = [];
            } else {
              return; // Can't select more
            }
          }
        }

        // Select
        card.classList.add("selected");
        this.selections[step].push({
          blockId,
          optionId,
          filterType,
          filterValue,
        });
      }

      // Update button state
      this.updateButtonState(step);
    }

    updateButtonState(step) {
      const stepElement = this.section.querySelector(
        `[data-quiz-step="${step}"]`,
      );
      if (!stepElement) return;

      const hasSelection = this.selections[step].length > 0;

      // Find the quiz-buttons div - it's INSIDE the step element
      const buttonsDiv = stepElement.querySelector(".quiz-buttons");

      if (buttonsDiv) {
        const nextBtn = buttonsDiv.querySelector("[data-quiz-next]");
        const resBtn = buttonsDiv.querySelector("[data-quiz-results]");

        if (nextBtn) nextBtn.disabled = !hasSelection;
        if (resBtn) resBtn.disabled = !hasSelection;
      }
    }

    nextStep() {
      if (this.currentStep >= this.config.stepCount) {
        return;
      }

      const currentStepElement = this.section.querySelector(
        `[data-quiz-step="${this.currentStep}"]`,
      );
      const nextStepElement = this.section.querySelector(
        `[data-quiz-step="${this.currentStep + 1}"]`,
      );

      if (!currentStepElement || !nextStepElement) {
        return;
      }

      // Update option visibility based on dependencies before showing next step
      this.updateOptionVisibility(this.currentStep + 1);

      // Update dynamic step titles (e.g., Step 2 title based on Step 1 selections)
      this.updateDynamicStepTitle(this.currentStep + 1);

      this.transitionStep(currentStepElement, nextStepElement, () => {
        this.currentStep++;
        this.updateProgressBar();
        this.updateButtonState(this.currentStep);
      });
    }

    previousStep() {
      if (this.currentStep <= 1) return;

      const currentStepElement = this.section.querySelector(
        `[data-quiz-step="${this.currentStep}"]`,
      );
      const prevStepElement = this.section.querySelector(
        `[data-quiz-step="${this.currentStep - 1}"]`,
      );

      if (!currentStepElement || !prevStepElement) return;

      // Update option visibility for the previous step
      this.updateOptionVisibility(this.currentStep - 1);

      this.transitionStep(currentStepElement, prevStepElement, () => {
        this.currentStep--;
        this.updateProgressBar();
      });
    }

    transitionStep(fromElement, toElement, callback) {
      if (this.config.enableLoadingAnimation) {
        // Fade out
        fromElement.classList.add("fade-out");

        setTimeout(() => {
          fromElement.style.display = "none";
          fromElement.classList.remove("fade-out");

          // Show and fade in
          toElement.style.display = "block";
          toElement.classList.add("fade-in");

          // Scroll to top of quiz section
          this.scrollToQuizTop();

          setTimeout(() => {
            toElement.classList.remove("fade-in");
            if (callback) callback();
          }, 400);
        }, 400);
      } else {
        fromElement.style.display = "none";
        toElement.style.display = "block";
        // Scroll to top of quiz section
        this.scrollToQuizTop();
        if (callback) callback();
      }
    }

    scrollToQuizTop() {
      // Scroll to the progress bar (or quiz section if progress bar not available)
      const targetElement = this.progressWrapper || this.section;
      const targetTop =
        targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offset = 20; // Small offset from the top

      window.scrollTo({
        top: targetTop - offset,
        behavior: "smooth",
      });
    }

    updateProgressBar() {
      if (!this.progressBar) {
        return;
      }

      const progress = ((this.currentStep - 1) / this.config.stepCount) * 100;
      this.progressBar.style.width = `${progress}%`;
    }

    /**
     * Update dynamic step titles based on previous selections
     * Uses titleOverrides from config to change step titles based on previous step selections
     */
    updateDynamicStepTitle(stepNumber) {
      const stepConfig = this.config.steps[stepNumber];
      if (
        !stepConfig ||
        !stepConfig.titleOverrides ||
        stepConfig.titleOverrides.length === 0
      ) {
        return;
      }

      const stepElement = this.section.querySelector(
        `[data-quiz-step="${stepNumber}"]`,
      );
      if (!stepElement) {
        return;
      }

      const titleElement = stepElement.querySelector("h4");
      if (!titleElement) {
        return;
      }

      // Store the original title on first access
      if (!titleElement.dataset.defaultTitle) {
        titleElement.dataset.defaultTitle = titleElement.textContent;
      }

      // Get previous step selections
      const prevSelections = this.selections[stepNumber - 1] || [];
      const selectedIds = prevSelections.map((sel) => sel.optionId);

      // Find matching override (only matches when exactly one option is selected)
      const match = stepConfig.titleOverrides.find(
        (o) => selectedIds.length === 1 && selectedIds[0] === o.optionId,
      );

      // Apply the title
      titleElement.textContent = match
        ? match.title
        : stepConfig.defaultTitle || titleElement.dataset.defaultTitle;
    }

    /**
     * Update option visibility based on dependencies from previous steps
     * Options with a depends_on attribute will only be shown if ANY of the
     * specified Option IDs were selected in any previous step
     *
     * Supported formats:
     *   - "optionId" - Contains: show if optionId is among selections
     *   - "id1, id2" - AND: show if both are among selections
     *   - "=optionId" - Exact: show if only optionId is selected
     *   - "id1 | id2" - OR: show if either group matches
     *   - "task_level:1" - Show if max task level is exactly 1
     *   - "task_level:2+" - Show if max task level is 2 or higher
     *   - "task_level:1-2" - Show if max task level is between 1 and 2
     */
    updateOptionVisibility(stepNumber) {
      const stepElement = this.section.querySelector(
        `[data-quiz-step="${stepNumber}"]`,
      );
      if (!stepElement) {
        return;
      }

      // Collect all selected option IDs and calculate max task level from ALL previous steps
      const selectedOptionIds = new Set();
      let maxTaskLevel = 0;

      for (let step = 1; step < stepNumber; step++) {
        const stepSelections = this.selections[step] || [];
        stepSelections.forEach((selection) => {
          // Collect option IDs
          if (selection.optionId) {
            const normalizedId = selection.optionId.toLowerCase().trim();
            selectedOptionIds.add(normalizedId);
          }

          // Track max task level from task_level filter types
          if (selection.filterType === "task_level") {
            const level = parseInt(selection.filterValue, 10);
            if (!isNaN(level)) {
              maxTaskLevel = Math.max(maxTaskLevel, level);
            }
          }
        });
      }

      // Get all option wrappers in this step
      const optionWrappers = stepElement.querySelectorAll(
        "[data-option-wrapper]",
      );

      optionWrappers.forEach((wrapper, index) => {
        const dependsOn = wrapper.dataset.dependsOn;
        const optionCard = wrapper.querySelector(".option-card");

        // If no dependency, always show
        if (!dependsOn || dependsOn.trim() === "") {
          wrapper.style.display = "";
          return;
        }

        // Parse dependency with support for OR groups (|) and AND within groups (,)
        // Two modes:
        //   - Contains (default): "trabalho" or "design, casa"
        //     Shows if the required IDs are AMONG the selections (other selections allowed)
        //   - Exact (prefix with =): "=trabalho" or "=design, casa"
        //     Shows if the required IDs are the ONLY selections (exact match)
        //
        // Example: "trabalho | =design, casa" means:
        //   - Show if "trabalho" is AMONG the selections, OR
        //   - Show if EXACTLY "design" AND "casa" are selected (nothing else)
        const orGroups = dependsOn.split("|").map((group) => group.trim());

        // Check if ANY of the OR groups are satisfied
        let matchedGroup = null;
        const isSatisfied = orGroups.some((group, groupIndex) => {
          // Check if this group requires exact match (starts with =)
          const isExactMatch = group.startsWith("=");
          const groupContent = isExactMatch ? group.substring(1) : group;

          // Split by comma to get individual conditions
          const conditions = groupContent
            .split(",")
            .map((c) => c.trim())
            .filter((c) => c !== "");

          if (conditions.length === 0) {
            return false;
          }

          // Separate task_level conditions from option ID conditions
          const taskLevelConditions = [];
          const optionIdConditions = [];

          conditions.forEach((condition) => {
            const taskLevelMatch = condition.match(
              /^task_level:(\d+)(\+|-(\d+))?$/i,
            );
            if (taskLevelMatch) {
              taskLevelConditions.push({
                original: condition,
                level: parseInt(taskLevelMatch[1], 10),
                modifier: taskLevelMatch[2], // "+", "-X", or undefined
                upperLevel: taskLevelMatch[3]
                  ? parseInt(taskLevelMatch[3], 10)
                  : null,
              });
            } else {
              optionIdConditions.push(condition.toLowerCase());
            }
          });

          // Check task level conditions (all must match)
          let taskLevelMatches = true;

          for (const tlc of taskLevelConditions) {
            let matches = false;

            if (tlc.modifier === "+") {
              matches = maxTaskLevel >= tlc.level;
            } else if (tlc.modifier && tlc.modifier.startsWith("-")) {
              matches =
                maxTaskLevel >= tlc.level && maxTaskLevel <= tlc.upperLevel;
            } else {
              matches = maxTaskLevel === tlc.level;
            }

            if (!matches) {
              taskLevelMatches = false;
            }
          }

          // Check option ID conditions
          let optionIdMatches = true;

          if (optionIdConditions.length > 0) {
            if (isExactMatch) {
              // Exact match: must have same count AND all required present
              const allPresent = optionIdConditions.every((id) =>
                selectedOptionIds.has(id),
              );
              const sameSize =
                selectedOptionIds.size === optionIdConditions.length;
              optionIdMatches = sameSize && allPresent;
            } else {
              // Contains match: all required IDs must be present
              const allPresent = optionIdConditions.every((id) =>
                selectedOptionIds.has(id),
              );
              optionIdMatches = allPresent;
            }
          }

          // Both task level AND option IDs must match (if both present)
          const matches = taskLevelMatches && optionIdMatches;

          if (matches) {
            matchedGroup = group;
          }
          return matches;
        });

        // Show or hide based on dependency
        if (isSatisfied) {
          wrapper.style.display = "";
        } else {
          wrapper.style.display = "none";
          // Also deselect the option if it was selected
          if (optionCard && optionCard.classList.contains("selected")) {
            optionCard.classList.remove("selected");
            const blockId = optionCard.dataset.optionBlock;
            this.selections[stepNumber] = this.selections[stepNumber].filter(
              (s) => s.blockId !== blockId,
            );
          }
        }
      });

      // Update button state after visibility changes (in case selections were removed)
      this.updateButtonState(stepNumber);
    }

    showResults() {
      if (this.config.enableLoadingAnimation) {
        this.showLoading();
      }

      // Calculate matches
      setTimeout(
        () => {
          this.calculateMatches();
          this.renderResults();
          this.pushQuizResultsToDataLayer();

          // Transition to results
          const currentStepElement = this.section.querySelector(
            `[data-quiz-step="${this.currentStep}"]`,
          );
          const resultsElement = this.section.querySelector(
            '[data-quiz-step="results"]',
          );

          if (this.config.enableLoadingAnimation) {
            this.hideLoading();
          }

          this.transitionStep(currentStepElement, resultsElement, () => {
            // Update progress to 100%
            if (this.progressBar) {
              this.progressBar.style.width = "100%";
            }
          });
        },
        this.config.enableLoadingAnimation ? 1500 : 0,
      );
    }

    pushQuizResultsToDataLayer() {
      window.dataLayer = window.dataLayer || [];

      const product = this.matchingProducts[0];
      if (!product) return;

      window.dataLayer.push({
        event: "quiz_submit",
        recommended_product: {
          item_id: product.id,
          item_name: product.title,
          item_variant: this.selectedVariants[product.id]?.title || null,
          price: product.price / 100,
          item_url: product.url,
        },
      });
    }

    calculateMatches() {
      let filteredProducts = [...this.products];

      // Filter by availability if enabled
      if (this.config.onlyAvailable) {
        filteredProducts = filteredProducts.filter((p) => p.available);
      }

      // TASK LEVEL LOGIC:
      // 1. Collect all task_level filter values from ALL selected options
      // 2. Find the MAXIMUM level required across all selections
      // 3. Pre-filter products that can handle that level
      let maxRequiredTaskLevel = 0;
      const nonTaskLevelSelections = {}; // Selections that are NOT task_level type

      for (let step = 1; step <= this.config.stepCount; step++) {
        const stepSelections = this.selections[step];
        if (stepSelections.length === 0) continue;

        nonTaskLevelSelections[step] = [];

        for (const selection of stepSelections) {
          // Skip "none" filter type - it's just for dependencies/branching, not product filtering
          if (selection.filterType === "none" || !selection.filterType) {
            continue;
          }

          if (selection.filterType === "task_level") {
            // Track the maximum task level required
            const level = parseInt(selection.filterValue, 10);
            if (!isNaN(level)) {
              maxRequiredTaskLevel = Math.max(maxRequiredTaskLevel, level);
            }
          } else {
            // Keep other filter types for normal filtering
            nonTaskLevelSelections[step].push(selection);
          }
        }
      }

      // Pre-filter products by task level if any task_level options were selected
      if (maxRequiredTaskLevel > 0) {
        filteredProducts = filteredProducts.filter((product) => {
          return this.productSupportsTaskLevel(product, maxRequiredTaskLevel);
        });
      }

      // Determine which steps have non-task_level selections
      const stepsWithSelections = [];
      for (let step = 1; step <= this.config.stepCount; step++) {
        if (
          nonTaskLevelSelections[step] &&
          nonTaskLevelSelections[step].length > 0
        ) {
          stepsWithSelections.push(step);
        }
      }

      // Calculate scores for each product using non-task_level filters
      // IMPORTANT: Between steps = AND logic (must match ALL steps)
      //            Within a step = uses filterLogic setting (OR/AND)
      const scoredProducts = filteredProducts.map((product) => {
        let score = 0;
        let matchDetails = [];
        let matchedSteps = 0;

        // Add base score for matching task level
        if (maxRequiredTaskLevel > 0) {
          score += 20; // Bonus for passing task level filter
          matchDetails.push(`TaskLevel${maxRequiredTaskLevel}:+20`);
        }

        // Go through each step's non-task_level selections
        for (let step = 1; step <= this.config.stepCount; step++) {
          const stepSelections = nonTaskLevelSelections[step] || [];
          if (stepSelections.length === 0) continue;

          const stepConfig = this.config.steps[step] || {};
          // filterLogic: OR = match ANY option within this step
          //              AND = match ALL options within this step
          const filterLogic = stepConfig.filterLogic || "OR";
          const scoreWeight = stepConfig.scoreWeight || 10;

          let stepMatches = [];
          for (const selection of stepSelections) {
            const matches = this.checkProductMatch(product, selection);
            stepMatches.push(matches);
          }

          // Apply filter logic WITHIN the step
          let stepPassed = false;
          let stepScore = 0;

          if (filterLogic === "AND") {
            // AND: ALL selected options in this step must match
            stepPassed = stepMatches.every((m) => m);
            if (stepPassed) {
              stepScore = scoreWeight;
            }
          } else {
            // OR: ANY selected option in this step can match
            const matchCount = stepMatches.filter((m) => m).length;
            stepPassed = matchCount > 0;
            if (stepPassed) {
              stepScore = scoreWeight * (matchCount / stepSelections.length);
            }
          }

          if (stepPassed) {
            matchedSteps++;
            matchDetails.push(`Step${step}:+${stepScore.toFixed(0)}`);
            score += stepScore;
          }
        }

        // Between steps = AND logic: must match ALL steps that have non-task_level selections
        // If there are no non-task_level selections, product passes (already filtered by task level)
        const matchesAllSteps =
          stepsWithSelections.length === 0 ||
          matchedSteps === stepsWithSelections.length;

        return {
          ...product,
          score: matchesAllSteps ? score : 0, // Zero score if doesn't match all steps
          matchDetails,
          matchesAllSteps,
        };
      });

      // Only include products that match ALL steps
      const productsWithScore = scoredProducts.filter(
        (p) => p.matchesAllSteps && p.score > 0,
      );

      // Sort by score (highest first), then by price (lowest first)
      const sortedProducts = productsWithScore.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.price - b.price;
      });

      // Store ALL matching products (for color variant filtering)
      this.allMatchingProducts = sortedProducts;

      // Deduplicate by base collection - show only one result card per base product
      // but with color swatches for all matching color variants
      const seenBaseCollections = new Set();
      const deduplicatedProducts = [];

      for (const product of sortedProducts) {
        const baseCollection =
          product.metafields?.["apple.apl_BaseProductCollection"];

        // If no base collection or we haven't seen this base collection yet, include it
        if (!baseCollection || !seenBaseCollections.has(baseCollection)) {
          if (baseCollection) {
            seenBaseCollections.add(baseCollection);
          }
          deduplicatedProducts.push(product);

          // Stop once we have enough unique results
          if (deduplicatedProducts.length >= this.config.resultsCount) {
            break;
          }
        }
      }

      this.matchingProducts = deduplicatedProducts;

      // Initialize selected variants
      this.matchingProducts.forEach((product) => {
        if (product.variants && product.variants.length > 0) {
          this.selectedVariants[product.id] = product.variants[0];
        }
      });
    }

    checkProductMatch(product, selection) {
      const { filterType, filterValue } = selection;

      switch (filterType) {
        case "none":
        case "":
        case undefined:
        case null:
          // "none" filter type always matches - it's just for dependencies/branching
          return true;

        case "tag":
          // Case-insensitive partial match (contains)
          const filterValueLower = filterValue.toLowerCase();
          return (
            product.tags &&
            product.tags.some((tag) =>
              tag.toLowerCase().includes(filterValueLower),
            )
          );

        case "metafield":
          // Format: namespace.key:value OR namespace.key:value1,value2,value3 (multiple values)
          const colonIndex = filterValue.indexOf(":");
          if (colonIndex === -1) return false;

          const metafieldKey = filterValue.substring(0, colonIndex);
          const metafieldValues = filterValue
            .substring(colonIndex + 1)
            .split(",")
            .map((v) => v.trim().toLowerCase().replace(/\s+/g, "")); // Remove all spaces from filter values

          if (product.metafields && product.metafields[metafieldKey]) {
            // Normalize product value: lowercase and remove spaces for comparison
            const productValue = String(product.metafields[metafieldKey])
              .toLowerCase()
              .replace(/\s+/g, ""); // Remove all spaces from product value

            // Check if product metafield value matches ANY of the filter values
            return metafieldValues.some(
              (v) => productValue === v || productValue.includes(v),
            );
          }
          return false;

        case "price":
          // Format: min-max (e.g., "0-1500" or "1500-" or "-1500")
          // Handle European format where . is thousands separator and , is decimal
          const parsePriceValue = (str) => {
            if (!str || str.trim() === "") return null;
            const normalized = str.replace(/\./g, "").replace(",", ".");
            const parsed = parseFloat(normalized);
            return isNaN(parsed) ? null : parsed;
          };

          // Split only on the first dash to handle negative numbers or ranges
          const dashIndex = filterValue.indexOf("-");
          let min, max;

          if (dashIndex === -1) {
            // No dash - treat as exact price (shouldn't happen, but handle it)
            min = parsePriceValue(filterValue) || 0;
            max = min;
          } else {
            const minStr = filterValue.substring(0, dashIndex);
            const maxStr = filterValue.substring(dashIndex + 1);
            min = parsePriceValue(minStr) ?? 0;
            max = parsePriceValue(maxStr) ?? Infinity;
          }

          const productPrice = product.price / 100; // Shopify prices are in cents
          const priceMatches = productPrice >= min && productPrice <= max;
          return priceMatches;

        case "task_level":
          // Task Level filtering - products must support the required task level or higher
          // Filter value format: just the level number (e.g., "1", "2", "3")
          // Product tags format: task_level_1, task_level_2, task_level_3
          // A product with task_level_3 can handle levels 1, 2, and 3
          // A product with task_level_1 can only handle level 1
          const requiredLevel = parseInt(filterValue, 10);
          if (isNaN(requiredLevel)) return false;

          // Find the highest task level the product supports from its tags
          let productMaxLevel = 0;
          if (product.tags) {
            for (const tag of product.tags) {
              const tagLower = tag.toLowerCase();
              // Match tags like "task_level_1", "task_level_2", "task_level_3"
              // Also support "tasklevel1", "tasklevel2", "tasklevel3" (no underscore)
              // Also support "level_1", "level_2", "level_3"
              const levelMatch = tagLower.match(
                /(?:task_?level_?|level_?)(\d+)/,
              );
              if (levelMatch) {
                const tagLevel = parseInt(levelMatch[1], 10);
                productMaxLevel = Math.max(productMaxLevel, tagLevel);
              }
            }
          }

          // Product matches if its max supported level is >= required level
          // e.g., a "Pro" product with task_level_3 can handle all tasks (levels 1, 2, 3)
          // e.g., a "Basic" product with task_level_1 can only handle basic tasks (level 1)
          return productMaxLevel >= requiredLevel;

        default:
          return false;
      }
    }

    /**
     * Helper method to get the task level from a product's tags.
     * Returns the highest task level found, or 0 if none.
     *
     * @param {Object} product - The product to check
     * @returns {number} - The highest task level found in tags (0 if none)
     */
    getProductTaskLevel(product) {
      if (!product.tags) return 0;

      let productMaxLevel = 0;

      for (const tag of product.tags) {
        const tagLower = tag.toLowerCase();
        // Match tags like "task_level_1", "task_level_2", "task_level_3"
        // Also support "tasklevel1", "tasklevel2", "tasklevel3" (no underscore)
        // Also support "level_1", "level_2", "level_3"
        const levelMatch = tagLower.match(/(?:task_?level_?|level_?)(\d+)/);
        if (levelMatch) {
          const tagLevel = parseInt(levelMatch[1], 10);
          productMaxLevel = Math.max(productMaxLevel, tagLevel);
        }
      }

      return productMaxLevel;
    }

    /**
     * Helper method to check if a product supports a given task level.
     * Used for pre-filtering products based on the maximum task level required.
     *
     * @param {Object} product - The product to check
     * @param {number} requiredLevel - The minimum task level required (1, 2, or 3)
     * @returns {boolean} - True if product supports the required level
     */
    productSupportsTaskLevel(product, requiredLevel) {
      if (!product.tags || requiredLevel <= 0) return true;

      const productMaxLevel = this.getProductTaskLevel(product);

      // If product has no task level tags, assume it can handle any level (backwards compatible)
      if (productMaxLevel === 0) return true;

      // Product matches if its max supported level is >= required level
      return productMaxLevel >= requiredLevel;
    }

    renderResults() {
      if (!this.resultsContainer) {
        return;
      }

      if (this.matchingProducts.length === 0) {
        this.resultsContainer.style.display = "none";
        if (this.emptyResults) {
          this.emptyResults.style.display = "block";
        }
        return;
      }

      this.resultsContainer.style.display = "block";
      if (this.emptyResults) {
        this.emptyResults.style.display = "none";
      }

      // Find lowest price product for automatic badge
      const lowestPriceProduct = [...this.matchingProducts].sort(
        (a, b) => a.price - b.price,
      )[0];

      let html = "";

      // Main product (first result)
      if (this.matchingProducts.length > 0) {
        const mainProduct = this.matchingProducts[0];
        const showMainBadge = this.shouldShowBadge(
          mainProduct,
          0,
          lowestPriceProduct,
        );
        html += this.renderMainProductCard(mainProduct, showMainBadge);
      }

      // Secondary products
      if (this.matchingProducts.length > 1) {
        html += '<div class="product-cards-secondary">';
        for (let i = 1; i < this.matchingProducts.length; i++) {
          const product = this.matchingProducts[i];
          const showBadge = this.shouldShowBadge(
            product,
            i,
            lowestPriceProduct,
          );
          html += this.renderSecondaryProductCard(product, showBadge);
        }
        html += "</div>";
      }

      this.resultsContainer.innerHTML = html;

      // Bind color swatch events
      this.bindColorSwatchEvents();

      // Bind buy button events
      this.bindBuyButtonEvents();
    }

    shouldShowBadge(product, index, lowestPriceProduct) {
      if (index === 0) {
        // Main product
        switch (this.config.mainBadgeMode) {
          case "automatic":
            return product.id === lowestPriceProduct.id;
          case "first":
            return true;
          default:
            return false;
        }
      } else {
        // Secondary products
        switch (this.config.secondaryBadgeMode) {
          case "automatic":
            return product.id === lowestPriceProduct.id;
          default:
            return false;
        }
      }
    }

    renderMainProductCard(product, showBadge) {
      // Get current display data (may be color variant or original product)
      const displayData = this.getProductDisplayData(product);

      return `
        <div class="product-card-main" data-product-id="${
          product.id
        }" data-base-collection="${
          product.metafields?.["apple.apl_BaseProductCollection"] || ""
        }">
          <div class="box bg-white rounded-4 p-4">
            <div class="row-quiz">
              <div class="text-center col-md-6 mb-3">
                <img src="${
                  displayData.imageUrl
                }" class="img-fluid product-Image" data-product-image="${
                  product.id
                }">
              </div>
              <div class="col-md-6 text-product-result">
                ${
                  showBadge
                    ? `<h6 class="text-uppercase">${this.config.badgeText}</h6>`
                    : ""
                }
                <h5 class="fw-bold mt-2" data-product-title="${product.id}">${
                  displayData.displayTitle
                }</h5>
                <p class="mb-1 price" data-product-price="${product.id}">${
                  displayData.price
                }</p>
                <p class="text-muted small mb-1" data-product-description="${
                  product.id
                }">${displayData.description}</p>
                ${this.renderColorSwatches(product)}
                <button class="btn btn-buy mt-3 px-4" data-buy-product="${
                  product.id
                }" data-product-url="${displayData.url}">
                  ${this.config.btnBuyText}
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderSecondaryProductCard(product, showBadge) {
      // Get current display data (may be color variant or original product)
      const displayData = this.getProductDisplayData(product);

      // Build the label - always show secondary label if set, or badge if applicable
      let labelHtml = '<div class="mt-3"></div>';
      if (this.config.secondaryLabelText) {
        labelHtml = `<h6 class="text-uppercase mt-3 secondary-label">${this.config.secondaryLabelText}</h6>`;
      } else if (showBadge) {
        labelHtml = `<h6 class="text-uppercase mt-3">${this.config.badgeText}</h6>`;
      }

      return `
        <div class="product-card-secondary" data-product-id="${
          product.id
        }" data-base-collection="${
          product.metafields?.["apple.apl_BaseProductCollection"] || ""
        }">
          <div class="p-4 text-start box rounded-4 bg-white text-product-result">
            <div class="text-center">
              <img src="${
                displayData.imageUrl
              }" class="img-fluid product-Image" data-product-image="${
                product.id
              }">
            </div>
            ${labelHtml}
            <h5 class="fw-bold mt-2" data-product-title="${product.id}">${
              displayData.displayTitle
            }</h5>
            <p class="mb-1 price" data-product-price="${product.id}">${
              displayData.price
            }</p>
            <p class="text-muted small mb-1" data-product-description="${
              product.id
            }">${displayData.description}</p>
            ${this.renderColorSwatches(product)}
            <button class="btn btn-buy mt-3 px-4" data-buy-product="${
              product.id
            }" data-product-url="${displayData.url}">
              ${this.config.btnBuyText}
            </button>
          </div>
        </div>
      `;
    }

    getProductDisplayData(product) {
      // Check if a different color variant is selected
      const selectedColorProductId = this.selectedColorProducts[product.id];
      const colorVariants = this.getColorVariantsForProduct(product);

      // Find the selected color variant or use current product
      let displayProduct = null;
      if (selectedColorProductId && colorVariants.length > 0) {
        displayProduct = colorVariants.find(
          (v) => v.id === selectedColorProductId,
        );
      }

      // If no variant selected, find the current product in variants or use product itself
      if (!displayProduct && colorVariants.length > 0) {
        displayProduct = colorVariants.find((v) => v.id === product.id);
      }

      // Use official product name if available, otherwise fall back to title
      const officialName =
        product.metafields?.["apple.apl_OfficialProductName"];
      const displayTitle = officialName || product.title;

      // Use Option2Value metafield as description
      // Fall back to displayProduct's Option2Value, then product title
      const activeProduct = displayProduct || product;
      const option2Value =
        activeProduct.metafields?.["apple.apl_Option2Value"] || "";
      const description = option2Value || activeProduct.title;

      return {
        displayTitle,
        description,
        imageUrl: displayProduct?.image || product.featured_image,
        price: displayProduct?.price_formatted || product.price_formatted,
        url: displayProduct?.url || product.url,
      };
    }

    renderColorSwatches(product) {
      // Get color variants from the base product collection (non-Shopify variants)
      const colorVariants = this.getColorVariantsForProduct(product);

      // If no color variants from base collection, don't show swatches
      if (!colorVariants || colorVariants.length <= 1) {
        return "";
      }

      // Find current selected color variant or default to this product
      const selectedColorId =
        this.selectedColorProducts[product.id] || product.id;

      let swatchesHtml =
        '<p class="fw-bold me-2 mb-2 mt-3">Colores disponibles</p><div class="colors">';

      colorVariants.forEach((colorVariant) => {
        const isSelected = colorVariant.id === selectedColorId;
        const swatchStyle = colorVariant.colorSwatch
          ? `background-image: url('${colorVariant.colorSwatch}'); background-size: cover;`
          : `background-color: #ccc;`;

        swatchesHtml += `
          <div class="color-option ${isSelected ? "selected" : ""}" 
               data-color-product-id="${colorVariant.id}"
               data-original-product-id="${product.id}"
               style="${swatchStyle}"
               title="${colorVariant.colorName || colorVariant.title}">
          </div>
        `;
      });

      swatchesHtml += "</div>";
      return swatchesHtml;
    }

    bindColorSwatchEvents() {
      this.resultsContainer
        .querySelectorAll(".color-option")
        .forEach((swatch) => {
          swatch.addEventListener("click", (e) => {
            const colorProductId = parseInt(swatch.dataset.colorProductId);
            const originalProductId = parseInt(
              swatch.dataset.originalProductId,
            );

            const product = this.matchingProducts.find(
              (p) => p.id === originalProductId,
            );
            if (!product) return;

            // Get the color variants for this product
            const colorVariants = this.getColorVariantsForProduct(product);
            const colorVariant = colorVariants.find(
              (v) => v.id === colorProductId,
            );
            if (!colorVariant) return;

            // Update selected color product
            this.selectedColorProducts[originalProductId] = colorProductId;

            // Update UI
            const productCard = this.resultsContainer.querySelector(
              `[data-product-id="${originalProductId}"]`,
            );
            if (productCard) {
              // Update image with the color variant's first image
              const img = productCard.querySelector(
                `[data-product-image="${originalProductId}"]`,
              );
              if (img && colorVariant.image) {
                img.src = colorVariant.image;
              }

              // Update price
              const priceEl = productCard.querySelector(
                `[data-product-price="${originalProductId}"]`,
              );
              if (priceEl && colorVariant.price_formatted) {
                priceEl.textContent = colorVariant.price_formatted;
              }

              // Update description (Option2Value metafield, fallback to title)
              const descEl = productCard.querySelector(
                `[data-product-description="${originalProductId}"]`,
              );
              if (descEl) {
                const option2Value =
                  colorVariant.metafields?.["apple.apl_Option2Value"] || "";
                descEl.textContent = option2Value || colorVariant.title;
              }

              // Update buy button URL
              const buyBtn = productCard.querySelector(
                `[data-buy-product="${originalProductId}"]`,
              );
              if (buyBtn && colorVariant.url) {
                buyBtn.dataset.productUrl = colorVariant.url;
              }

              // Update swatch selection
              productCard.querySelectorAll(".color-option").forEach((s) => {
                s.classList.remove("selected");
              });
              swatch.classList.add("selected");
            }
          });
        });
    }

    bindBuyButtonEvents() {
      this.resultsContainer
        .querySelectorAll("[data-buy-product]")
        .forEach((btn) => {
          btn.addEventListener("click", (e) => {
            const productId = parseInt(btn.dataset.buyProduct);
            const product = this.matchingProducts.find(
              (p) => p.id === productId,
            );
            if (!product) return;

            const selectedVariant =
              this.selectedVariants[productId] || product.variants?.[0];

            if (this.config.buyButtonAction === "add_to_cart") {
              this.addToCart(selectedVariant?.id || product.variants?.[0]?.id);
            } else {
              // Go to product page
              const url = selectedVariant?.url || product.url;
              window.location.href = url;
            }
          });
        });
    }

    addToCart(variantId) {
      if (!variantId) return;

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          // Trigger cart update event
          document.dispatchEvent(
            new CustomEvent("cart:updated", { detail: data }),
          );

          // Optional: Show success message or open cart drawer
          // This depends on the theme's cart implementation
        })
        .catch((error) => {
          console.error("Error adding to cart:", error);
        });
    }

    showLoading() {
      if (this.loadingOverlay) {
        this.loadingOverlay.style.display = "flex";
      }
    }

    hideLoading() {
      if (this.loadingOverlay) {
        this.loadingOverlay.style.display = "none";
      }
    }

    restart() {
      // Reset selections
      for (let i = 1; i <= this.config.stepCount; i++) {
        this.selections[i] = [];
      }

      // Reset UI
      this.section.querySelectorAll(".option-card.selected").forEach((card) => {
        card.classList.remove("selected");
      });

      // Reset to first step
      const resultsElement = this.section.querySelector(
        '[data-quiz-step="results"]',
      );
      const firstStepElement = this.section.querySelector(
        '[data-quiz-step="1"]',
      );

      if (resultsElement && firstStepElement) {
        this.transitionStep(resultsElement, firstStepElement, () => {
          this.currentStep = 1;
          this.updateProgressBar();
          this.updateButtonState(1);
        });
      }

      // Clear results
      this.matchingProducts = [];
      this.selectedVariants = {};
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = "";
      }
    }
  }

  // Initialize all quiz sections on the page
  function initProductQuizzes() {
    document.querySelectorAll(".product-quiz").forEach((section) => {
      new ProductQuiz(section);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProductQuizzes);
  } else {
    initProductQuizzes();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target && typeof event.target.querySelector === "function") {
      const section = event.target.querySelector(".product-quiz");
      if (section) {
        new ProductQuiz(section);
      }
    }
  });
})();
